import { createApi } from '@reduxjs/toolkit/query/react'
import { staggeredBaseQueryWithBailOut } from '../libs/services'
import { getSystemHeaders } from '../libs/system'
import { ICD10 } from '../model/icd10'
import { FilterParams, unversionedApiUrl } from './common'

const TAG = 'icd10-system-api'
const TAG_FAV = 'icd10-system-fav-api'

type Icd10Code = string

export const icd10Api = createApi({
	reducerPath: 'icd10Api',
	tagTypes: [TAG, TAG_FAV],
	baseQuery: staggeredBaseQueryWithBailOut(unversionedApiUrl),
	endpoints: builder => ({
		getIcd10ByCode: builder.query<ICD10, Icd10Code>({
			query: code => ({
				url: `/system/v2/icd10/find/Code/${code}`,
				headers: getSystemHeaders(),
			}),
			providesTags: [TAG],
		}),
		getMultipleIcd10ByCode: builder.query<ICD10[], Icd10Code>({
			query: codes => {
				let validCodes = typeof codes === 'string' ? codes.split(',') : codes

				return {
					url: `/v1/icd10/codes`,
					method: 'POST',
					body: {
						codes: validCodes,
					},
					headers: getSystemHeaders(),
				}
			},
			providesTags: [TAG],
		}),
		getFilteredIcd10: builder.query<ICD10[], FilterParams>({
			query: ({
				filter,
				propNames = ['Code', 'Description'],
				favourites,
				upriseOrgId,
			}: FilterParams) => ({
				url: !favourites
					? `/system/v2/icd10/paginated?q=${encodeURIComponent(
							filter,
					  )}&propNames=${propNames.join(',')}`
					: '/system/v2/icd10/favourites',
				headers: getSystemHeaders(upriseOrgId),
			}),
			providesTags: (result, error, query) => [
				query.favourites ? TAG_FAV : TAG,
			],
		}),
		setIcd10Favourite: builder.mutation<
			ICD10,
			{ code: string; favourite: boolean; upriseOrgId?: string }
		>({
			query: ({ code, favourite, upriseOrgId }) => ({
				url: `/v1/users/icd10/${code}/favourite`,
				method: 'PUT',
				headers: getSystemHeaders(upriseOrgId),
				body: {
					favourite,
				},
			}),
			invalidatesTags: [TAG, TAG_FAV],
		}),
	}),
})

export const {
	useGetIcd10ByCodeQuery,
	useGetFilteredIcd10Query,
	useSetIcd10FavouriteMutation,
	useGetMultipleIcd10ByCodeQuery,
} = icd10Api
